import type { AlertEventResponse, TelemetryDailyPoint, TelemetryLatestMetric } from '../api/types';

/** Tingkat kesehatan alat untuk badge ringkas. */
export type HealthLevel = 'ok' | 'warning' | 'critical' | 'unknown';

export const HEALTH_LABEL: Record<HealthLevel, string> = {
  ok: 'Sehat',
  warning: 'Perhatian',
  critical: 'Kritis',
  unknown: 'Tak ada data',
};

/**
 * Kesehatan diturunkan dari metrik `status` TERKINI (agen mengirim 'online'/'degraded'/'offline').
 * Ambang numerik adalah milik SERVER (aturan alert) — klien tidak menebak sehat/tidak dari angka
 * mentah, hanya memetakan status eksplisit yang dilaporkan agen.
 */
export function statusKesehatan(latest: readonly TelemetryLatestMetric[]): HealthLevel {
  const status = latest.find((m) => m.metricKey === 'status')?.metricText?.toLowerCase();
  if (status === undefined) return 'unknown';
  if (status === 'online' || status === 'ok' || status === 'healthy') return 'ok';
  if (status === 'degraded' || status === 'warning') return 'warning';
  return 'critical'; // offline / error / nilai tak dikenal = perlakukan sebagai kritis
}

/** Jumlah kejadian alert yang BELUM pulih (resolved_at null). */
export function jumlahAlertTerbuka(events: readonly AlertEventResponse[]): number {
  return events.reduce((n, e) => n + (e.resolvedAt === null ? 1 : 0), 0);
}

/**
 * Ambang TAMPILAN default untuk menandai alat "basi/offline": pembacaan terakhir lebih tua dari ini.
 * Ini konstanta tampilan klien (bukan aturan server) — kalau agen diam >15 menit, metrik `status`
 * terakhirnya ("online") sudah usang dan tak boleh ditampilkan seolah alat sehat. Alerting offline
 * PROAKTIF (tanpa seseorang membuka dashboard) adalah pekerjaan server tersendiri; ini sekadar
 * jujur pada apa yang terlihat saat kartu dibuka.
 */
export const BATAS_BASI_MS = 15 * 60 * 1000;

/** Waktu (epoch ms) pembacaan TERBARU di antara metrik latest; null bila kosong / tak ada yang valid. */
export function waktuPembacaanTerbaru(latest: readonly TelemetryLatestMetric[]): number | null {
  let terbaru: number | null = null;
  for (const m of latest) {
    const t = Date.parse(m.recordedAt);
    if (!Number.isNaN(t) && (terbaru === null || t > terbaru)) terbaru = t;
  }
  return terbaru;
}

/**
 * Apakah telemetri alat sudah "basi" — pembacaan terbaru lebih tua dari `batasMs` relatif `now`.
 * Latest kosong → false (itu keadaan "belum ada data", bukan "basi"; ditangani terpisah).
 */
export function dataBasi(
  latest: readonly TelemetryLatestMetric[],
  now: Date,
  batasMs: number = BATAS_BASI_MS,
): boolean {
  const terbaru = waktuPembacaanTerbaru(latest);
  if (terbaru === null) return false;
  return now.getTime() - terbaru > batasMs;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Titik `polyline` SVG untuk sparkline dari seri nilai (null = jeda, dilewati). x dibagi rata
 * sepanjang `width` menurut INDEKS asli (jeda tetap memakan ruang x-nya), y dinormalisasi min..max
 * ke `height` (atas = nilai tertinggi). Kosong → ''; seri datar → garis di tengah.
 */
export function sparklinePoints(
  values: readonly (number | null)[],
  width: number,
  height: number,
): string {
  const pts = values
    .map((v, i) => ({ i, v }))
    .filter((p): p is { i: number; v: number } => typeof p.v === 'number' && Number.isFinite(p.v));
  if (pts.length === 0) return '';
  const n = values.length;
  const x = (i: number): number => (n <= 1 ? width / 2 : (i / (n - 1)) * width);
  const lo = Math.min(...pts.map((p) => p.v));
  const hi = Math.max(...pts.map((p) => p.v));
  const y = (v: number): number =>
    hi === lo ? height / 2 : height - ((v - lo) / (hi - lo)) * height;
  return pts.map((p) => `${round2(x(p.i))},${round2(y(p.v))}`).join(' ');
}

/** Nilai `avgValue` seri harian sebagai urutan angka/null untuk sparkline. */
export function seriRataHarian(series: readonly TelemetryDailyPoint[]): (number | null)[] {
  return series.map((p) => p.avgValue);
}

/** Label manusiawi + satuan untuk sebuah metrik. */
export function formatMetrik(key: string, value: number | null, text: string | null): string {
  if (text !== null) return text;
  if (value === null) return '—';
  const bulat = round2(value);
  switch (key) {
    case 'cpu_percent':
    case 'memory_percent':
    case 'disk_percent':
      return `${bulat}%`;
    case 'temperature_c':
      return `${bulat}°C`;
    case 'uptime_seconds':
      return `${Math.floor(value / 3600)} jam`;
    default:
      return String(bulat);
  }
}
